import "@scss/components/reset-calendar.scss";

import { $UserAddPermissions, ACTIONS } from "@store/user-store";
import { $Warning, $WarningStates, FetchWarning } from "@store/warning-store";
import { TabItems } from "@components/common";
import { setTitle } from "@store/title-store";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useWorkspaceGroupsQuery } from "@lib/workspace";
import { WorkspaceDirectoryShortDataT } from "@interfaces/company/workspace";
import { TitleProps } from "@store/title-store";
import { $Departments } from "@store/company/departments-store";
import { $Employees } from "@store/company/employees-store";
import { $SelectedPrescription } from "@store/prescription-store";

import ArchiveIcon from "@pages/company/archive";
import Companies from "./companies";
import CompanyInfo from "@pages/company/company-info";
import CompanyEdit from "@pages/company/company-edit";
import CreatePrescription from "@pages/prescription/create-prescription";
import Departments from "@pages/company/departments";
import { EditRequestTraining } from "@feature/request-training/edit-request-training";
import Employees from "@pages/company/employees";
import EmployeesCreate from "./employees/employees-create";
import DepartureSchedule from "./departure-schedule";
import CreateDepartureSchedule from "@pages/departure-schedule/create-departure-schedule";
import SingleDepartureSchedule from "@pages/departure-schedule/single-departure-schedule";
import EventCalendar from "@pages/company/calendar";
import Prescriptions from "@pages/prescription/prescriptions";
import ProcedureControlBriefings from "@pages/company/procedure-control-briefings";
import ProcedureControlTraning from "@pages/company/procedure-control-traning";
import RequestTrainingPage from "./request-training";
import SigningControl from "@pages/company/signing-control";
import SingleDepartment from "@pages/company/single-department";
import SingleDocumentsPage from "./single-documents";
import SinglePositionPage from "./single-position";
import SinglePrescription from "@pages/prescription/single-prescription";
import Workspace from "@pages/company/workspace";
import { useSelectedCompanyQuery } from "@lib/company";
import { useUnit } from "effector-react";
import { WorkplanPage } from "@feature/workplan";
import ProcedureControlMedicalExams from "./procedure-control-medical-exams";

const pathnameToTitle: { [key: string]: string } = {
  "/company/companies": "Компании",
  "/company": "Информация об организации",
  "/company/workspace": "Рабочий блок",
  "/company/departments": "Подразделения",
  "/company/employees": "Сотрудники",
  "/company/calendar": "Календарь",
  "/company/workplan": "План работы",
  "/company/signing-control": "Контроль подписания документов",
  "/company/procedure-control": "Контроль процедур",
  "/company/request-training": "Заявки на обучение",
  "/company/prescriptions": "Предписания",
  "/company/departure-schedule": "График выездов",
  "/company/archive": "Архив",
};

const Company = () => {
  const { company } = useSelectedCompanyQuery();
  const permissions = useUnit($UserAddPermissions);

  const { pathname, search } = useLocation();

  const { groups } = useWorkspaceGroupsQuery(company?.id);
  const departments = useUnit($Departments);
  const employees = useUnit($Employees);
  const prescription = useUnit($SelectedPrescription);
  const [paths, setPaths] = useState<WorkspaceDirectoryShortDataT[]>([]);
  const [titleChild, setTitleChild] = useState<string>();

  useEffect(() => {
    const objPathnameToTitle = Object.keys(pathnameToTitle)
      .sort((a, b) => a.length - b.length)
      .reverse()
      .find((e) => pathname.indexOf(e) !== -1);
    const newTitle =
      pathnameToTitle[pathname] ??
      pathnameToTitle[objPathnameToTitle!] ??
      "Компания";

    const title = [
      { title: "Главная", href: "/" },
      { title: newTitle, href: objPathnameToTitle ?? pathname },
    ] as TitleProps[];
    const urlParams = new URLSearchParams(search);

    if (urlParams.has("group_id")) {
      const group_id = parseInt(urlParams.get("group_id")!);
      if (group_id === 0) {
        title.push({
          title: "Общие документы организации",
          href: `${pathname}?group_id=${urlParams.get("group_id")}`,
        });
      } else {
        const group = groups?.find((e) => e.id === group_id && e.isAvailable);
        if (group)
          title.push({
            title: group.name,
            href: `${pathname}?group_id=${urlParams.get("group_id")}`,
          });
      }

      if (urlParams.has("folder_id") && paths.length) {
        paths
          .filter((e) => e !== undefined)
          .forEach((e, i) => {
            const disabled =
              i < paths.filter((e) => e !== undefined).length - 2;
            title.push({
              title: e.name,
              href: `${pathname}?group_id=${urlParams.get(
                "group_id"
              )}&folder_id=${e.id}`,
              disabled,
            });
          });
      }
    } else if (pathname.indexOf("company/departments") !== -1) {
      const ids = pathname.split("/").filter((e) => parseInt(e));
      if (ids) {
        const department = departments.find((e) => e.id === +ids[0]);
        if (department) {
          title.push({
            title: department.title,
            href: `/company/departments/${department.id}`,
          });
          if (ids.length > 1) {
            const position = department.positions.find((e) => e.id === +ids[1]);
            if (position)
              title.push({
                title: position.title,
                href: `/company/departments/${department.id}/${position.id}`,
              });
          }
        }
      }
    } else if (pathname.indexOf("company/employees") !== -1) {
      const id = pathname
        .split("/")
        .filter((e) => parseInt(e))
        .shift();
      const employee = employees.find((e) => e.id === +id!);
      if (employee)
        title.push({
          title: employee.name,
          href: `/company/employees/${employee.id}`,
        });
    } else if (pathname.indexOf("company/procedure-control") !== -1) {
      switch (pathname.split("/").pop()) {
        case "training":
          title.push({
            title: "Контроль обучения",
            href: `/company/procedure-control/training`,
          });
          break;
        case "briefings":
          title.push({
            title: "Контроль инструктажей",
            href: `/company/procedure-control/briefings`,
          });
          break;
        case "medical-exams":
          title.push({
            title: "Контроль медицинских осмотров",
            href: `/company/procedure-control/medical-exams`,
          });
          break;
      }
    } else if (pathname.indexOf("company/request-training") !== -1) {
      const id = pathname
        .split("/")
        .filter((e) => parseInt(e))
        .shift();
      if (!id) setTitleChild(undefined);
      if (id && titleChild) title.push({ title: titleChild, href: pathname });
    } else if (pathname.indexOf("company/prescriptions") !== -1) {
      const id = pathname
        .split("/")
        .filter((e) => parseInt(e))
        .shift();
      if (id && prescription){
        title.push({
          title: `#${prescription?.id}/${prescription?.companyId}`,
          href: `/company/prescriptions/${prescription!.id}`,
        });
      }
    }

    setTitle(title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, search, titleChild]);

  const TAB_ITEMS = useMemo(() => {
    const tabs = [
      { label: "Компании", path: "/company/companies" },
      { label: "Информация об организации", path: "/company", exact: true },
      { label: "Рабочий блок", path: "/company/workspace" },
      { label: "Подразделения", path: "/company/departments" },
      { label: "Сотрудники", path: "/company/employees" },
      { label: "Календарь", path: "/company/calendar" },
    ];
    if (
      permissions.hasPermission(ACTIONS.companySpecialistWorkPlan_allowedToShow)
    ) {
      tabs.push({ label: "План работы", path: "/company/workplan" });
    }
    if (
      permissions.hasPermission(
        ACTIONS.companyDocumentSigningControl_allowedToShow
      )
    ) {
      tabs.push({
        label: "Контроль подписания документов",
        path: "/company/signing-control",
      });
    }
    if (
      permissions.hasPermission(ACTIONS.companyProcedureControls_allowedToShow)
    ) {
      tabs.push({
        label: "Контроль процедур",
        path: "/company/procedure-control/training",
      });
    }
    if (
      permissions.hasPermission(
        ACTIONS.companyApplicationsForTraining_allowedToShow
      )
    ) {
      tabs.push({
        label: "Заявки на обучение",
        path: "/company/request-training",
      });
    }
    if (
      permissions.hasPermission(ACTIONS.companyDepartureSchedule_allowedToShow)
    ) {
      tabs.push({
        label: "Выезды",
        path: "/company/departure-schedule",
      });
    }
    tabs.push({
      label: "Предписания",
      path: "/company/prescriptions",
    });

    const hasArchiveTab = tabs.find((item) => item.label === "Архив");
    if (!hasArchiveTab) {
      tabs.push({ label: "Архив", path: "/company/archive" });
    }

    return tabs;
  }, [permissions]);
  const warning = useUnit($Warning);
  const warningState = useUnit($WarningStates);

  useEffect(() => {
    if (!company) return;
    FetchWarning(company.id);
    setTitle([
      { title: "Главная", href: "/" },
      { title: "Компании", href: "/company/companies" },
    ]);
  }, [company]);

  return (
    <main className="content-container company">
      <div className="content-section">
        <div className="tab-section">
          <div className="tab-cont">
            <TabItems
              items={TAB_ITEMS}
              warning={warning}
              warningLoading={warningState.isLoading}
              currentPath={pathname}
            />
            <div className="tab-content-items">
              <Routes>
                <Route path="/" Component={CompanyInfo} />
                <Route path="/edit" Component={CompanyEdit} />
                <Route path="/companies" Component={Companies} />
                <Route path="/workspace/*" element={<Workspace setPathsMain={setPaths} />} />
                <Route path="/departments" Component={Departments} />
                <Route path="/departments/:id" Component={SingleDepartment} />
                <Route path="/departments/:id/:id" Component={SinglePositionPage} />
                <Route path="/departments-documents/:id/:id" Component={SingleDocumentsPage} />
                <Route path="/employees" Component={Employees} />
                <Route path="/employees/create" element={<EmployeesCreate type="create" />} />
                <Route path="/:departmentId/employees/create" Component={EmployeesCreate} />
                <Route path="/:departmentId/employees/create" Component={EmployeesCreate} />
                <Route path="/employees/:id" element={<EmployeesCreate type="edit" />} />
                <Route path="/calendar" element={
                  <EventCalendar
                    isCompanyDefined={true}
                    defaultCompanyId={company?.id!}
                  />
                }/>
                <Route path="/archive" Component={ArchiveIcon} />
                <Route path="/workplan" Component={WorkplanPage} />
                <Route path="/signing-control" Component={SigningControl} />
                <Route path="/procedure-control" element={<Navigate to="/company/procedure-control/training" />} />
                <Route path="/procedure-control/training" Component={ProcedureControlTraning} />
                <Route path="/procedure-control/briefings" Component={ProcedureControlBriefings} />
                <Route path="/procedure-control/medical-exams" Component={ProcedureControlMedicalExams} />
                <Route path="/request-training" Component={RequestTrainingPage} />
                <Route path="/request-training/:id" element={<EditRequestTraining setTitle={setTitleChild} />} />
                <Route path="/prescriptions" Component={Prescriptions} />
                <Route path="/prescriptions/create" Component={CreatePrescription} />
                <Route path="/prescriptions/:id" Component={SinglePrescription} />
                <Route path="/prescriptions/:id/edit" Component={CreatePrescription} />
                {permissions.hasPermission(ACTIONS.companyDepartureSchedule_allowedToShow) && (
                  <Route path="/departure-schedule" Component={DepartureSchedule} />
                )}
                {/* {permissions.hasPermission(ACTIONS.companyDepartureSchedule_allowedToCreate) && (
                  <Route path="/departure-schedule/create" Component={CreateDepartureSchedule} />
                )} */}
                {/* {permissions.hasPermission(ACTIONS.companyDepartureSchedule_allowedToShow) && (
                  <Route path="/departure-schedule/:id" Component={SingleDepartureSchedule} />
                )}
                {permissions.hasPermission(ACTIONS.companyDepartureSchedule_allowedToEdit) && (
                  <Route path="/departure-schedule/:id/edit" Component={CreateDepartureSchedule} />
                )} */}
              </Routes>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Company;
